import React, { useCallback, useEffect, useState } from "react";
import FIltersDropdown from "../components/Filters/FiltersDropdown";
import MainSearchBar from "../components/MainSearchbar";
import { useScreenWidth } from "../custom-hooks/useScreenWidth";
import { enUS } from "date-fns/locale";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { format, parse, startOfWeek, getDay } from "date-fns";
import {
  ArrowRightEndOnRectangleIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { MapPinIcon } from "@heroicons/react/20/solid";
import CustomButton from "../components/CustomButton";
import {
  useGetAllAssessmentsForCalendar,
  useGetprovidersListForFilters,
} from "../custom-hooks/queries";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { isBackendViewOnlyUser, useUserRole } from "../utils/helperMethods";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { setSearchTextByPage } from "../redux-store/features/providerFiltersSlice";
import Pagination from "../components/CustomPagination";
import { getVQALocationFromProvider } from "../utils/addressUtils";
import CustomTooltip from "../components/Filters/CustomTooltip";

const AssessmentCalendar = () => {
  const screenWidth = useScreenWidth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userRole = useUserRole();
  const { user } = useUser();
  const subRoles = user?.publicMetadata?.sub_role || [];
  const isAdmin = isBackendViewOnlyUser(userRole);
  const hideRequestActions = isAdmin;

  // Fetch providers for filters
  const { data: providers } = useGetprovidersListForFilters();

  // Define filter options
  const filters = [
    {
      id: "docType",
      name: "Vendor Type",
      options: [
        { docId: "CDMO", label: "CDMO", checked: false },
        { docId: "CRO", label: "CRO", checked: false },
        { docId: "Medical Device", label: "Medical Device", checked: false },
        {
          docId: "Non-Profit/Government",
          label: "Non-Profit/Government",
          checked: false,
        },
        { docId: "Other", label: "Other", checked: false },
        {
          docId: "Site/Site Network",
          label: "Site/Site Network",
          checked: false,
        },
        {
          docId: "Technology/Service Provider",
          label: "Technology/Service Provider",
          checked: false,
        },
      ],
      total: 2,
    },
    {
      id: "provider",
      name: "Vendor",
      options: providers,
      total: 1335,
    },
    {
      id: "audit",
      name: "Assessment Type",
      options: [
        { statusID: "onsite", label: "On-Site", checked: false },
        { statusID: "remote", label: "Remote", checked: false },
      ],
      total: 5,
    },
  ];

  // Local state for selected filters
  const [selectedIds, setSelectedIds] = useState({
    docType: [],
    provider: [],
    audit: [],
  });

  // Configure date-fns localizer for the calendar
  const locales = { "en-US": enUS };
  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () => startOfWeek(new Date(), { locale: enUS }),
    getDay,
    locales,
  });

  // Get search query from Redux
  const searchQuery = useSelector(
    (state) => state.providersFilters.searchTextByPage["assessmentCalendar"]
  );

  // Pagination & current month state
  const [currentPage, setCurrentPage] = useState(1);
  const [currentMonth, setCurrentMonth] = useState(
    moment().utc().startOf("month").format("YYYY-MM-DD")
  );

  // Update current month based on calendar navigation
  const handleMonthChange = (date) => {
    const newMonth = moment(date).utc().startOf("month").format("YYYY-MM-DD");
    setCurrentMonth(newMonth);
  };

  // Debounce search submissions to avoid unnecessary calls
  const debouncedOnSubmit = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({
          pageKey: "assessmentCalendar",
          searchText: search,
        })
      );
    }, 300),
    [dispatch]
  );

  const onSubmit = (search) => {
    debouncedOnSubmit(search);
  };

  // Fetch assessments using currentMonth and filter criteria.
  const {
    data: requestList,
    isLoading,
    isError,
    refetch,
  } = useGetAllAssessmentsForCalendar(
    currentPage, // current_page
    [], // auditor_id_list
    selectedIds.provider, // provider_id_list
    null, // vqa_status
    null, // rfi_id_list
    selectedIds.docType, // provider_types
    currentMonth, // vqa_month (updated with calendar navigation)
    null, // provider_type
    selectedIds.audit, // assessment_type
    searchQuery // search
  );

  // Refetch assessments when currentMonth changes
  useEffect(() => {
    if (currentMonth) {
      refetch();
    }
  }, [currentMonth, refetch]);

  // Build calendar events from the API response.
  const [calendarEvents, setCalendarEvents] = useState([]);
  useEffect(() => {
    if (requestList?.items) {
      const events = requestList.items
        .filter(
          (item) => item.assessment_start_date && item.is_multi_sponsor_assessment
        )
        .map((item) => {
          // Use only the date part (YYYY-MM-DD) to create a local date object, ignoring time and UTC
          const dateStr = item.assessment_start_date.slice(0, 10); // 'YYYY-MM-DD'
          const [year, month, day] = dateStr.split('-').map(Number);
          const assessmentDate = new Date(year, month - 1, day); // JS Date: month is 0-based
          return {
            id: item.vqa_id,
            request_id: item.vqa_request_id,
            title: item.provider_name,
            start: assessmentDate,
            end: assessmentDate,
            is_already_requested: item.is_already_requested,
            is_sponsor_included: item.is_sponsor_included,
            is_assessment_closed: item.is_assessment_closed,
            assessment_sponsor_count: item.assessment_sponsor_count,
          };
        });
      setCalendarEvents(events);
    }
  }, [requestList]);

  // Calendar event style: red if assessment is closed; green otherwise.
  const eventStyleGetter = (event) => {
    const backgroundColor = event.is_assessment_closed ? "red" : "green";
    return {
      style: {
        backgroundColor,
        borderRadius: "4px",
        color: "white",
        border: "none",
        height: "23px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    };
  };

  // Handle event click to navigate to details page with extra state.
  const handleEventClick = (event) => {
    navigate(isAdmin
      ? `/Backend/Assessment/${event.request_id}/Manage`
      : `/joinVqa/${event.id}/Details`, {
      state: {
        is_already_requested: event.is_already_requested,
        is_sponsor_included: event.is_sponsor_included,
        is_assessment_closed: event.is_assessment_closed,
        assessment_sponsor_count: event.assessment_sponsor_count,
      },
    });
  };

  // Optionally sort the returned items by assessment_start_date.
  const sortedItems =
    requestList?.items?.sort(
      (a, b) =>
        new Date(a.assessment_start_date) - new Date(b.assessment_start_date)
    ) || [];
  const [openDropdownId, setOpenDropdownId] = useState(null);

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen pb-16">
      <div className="sm:flex sm:items-center"></div>
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-5">
        <h1 className="font-semibold">Audit Calendar</h1>
        <p>
          The below table displays all past and upcoming onsite and virtual
          audits.
        </p>
      </div>
      <div className="mt-6 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-5">
              <MainSearchBar
                pageKey="assessmentCalendar"
                placeholder="Search ..."
                height="40px"
                width={
                  screenWidth <= 375
                    ? "17.5rem"
                    : screenWidth < 425
                    ? "22rem"
                    : "25rem"
                }
                marginRight="mr-2"
                isSearchIcon={true}
              />
              <FIltersDropdown
                setSelectedIds={setSelectedIds}
                selectedIds={selectedIds}
                filters={filters}
              />
            </div>
            {/* Use a 2-column grid for equal widths */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* List Column */}
              <div className="text-sm leading-6 border-r pr-8 divide-y divide-gray-300 overflow-y-auto h-[650px]">
                {isLoading ? (
                  <div className="space-y-4 py-10">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className="flex gap-3 items-center animate-pulse"
                      >
                        <div className="bg-gray-300 w-10 h-10 rounded-full"></div>
                        <div className="flex flex-col flex-grow space-y-2">
                          <div className="h-4 bg-gray-300 rounded w-40"></div>
                          <div className="h-3 bg-gray-300 rounded w-32"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : sortedItems.length > 0 ? (
                  sortedItems
                    .filter((item) => item.is_multi_sponsor_assessment)
                    .map((item) => (
                      <div className="py-8 flex" key={item.vqa_id}>
                        <div className="bg-purple-200 text-purple-600 w-10 h-10 text-xl font-bold rounded-full flex items-center justify-center flex-shrink-0">
                          {item?.provider_name.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex flex-col flex-grow ml-4">
                          <h1 className="font-semibold truncate mb-1">
                            {item?.provider_name}
                          </h1>
                          <div className="flex flex-col gap-1 text-gray-500">
                            <span className="flex items-center gap-1">
                              <span>
                                {moment
                                  .utc(item?.assessment_start_date)
                                  .format("MMM D, YYYY")}
                              </span>
                            </span>
                            <span className="flex items-center gap-1">
                              <ArrowRightEndOnRectangleIcon className="w-4 h-4" />
                              <span>
                                {item?.vqa_type === "remote"
                                  ? "Remote"
                                  : "On-site"}
                              </span>
                            </span>
                            <span
                              className="flex items-center gap-1"
                              // TODO: Workaround to display old location
                              title={item?.provider_address1?.split(", ")?.length > 2
                                  ? item?.provider_address
                                  : getVQALocationFromProvider(item, { short: true, abbvState: true })}
                              style={{ maxWidth: "320px", overflow: "hidden" }}
                            >
                              <MapPinIcon className="w-4 h-4" />
                              <span
                                className="truncate block"
                                style={{ maxWidth: "270px" }}
                              >
                                {/* TODO: Workaround to display old location */}
                                {(() => {
                                  const location = getVQALocationFromProvider(item, { short: true, abbvState: true });
                                  const fullLocation = getVQALocationFromProvider(item, { short: false, abbvState: true });

                                  return <CustomTooltip forceTooltip={true} cutOffAfter={40} sliceLength={40} toolTipContent={fullLocation}>
                                    {location}
                                  </CustomTooltip>
                                })()}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="relative flex items-center ml-2">
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId((prev) =>
                                prev === item.vqa_id ? null : item.vqa_id
                              );
                            }}
                          >
                            <EllipsisHorizontalIcon className="w-6 h-6 text-gray-500 cursor-pointer" />
                          </div>
                          {openDropdownId === item.vqa_id && (
                            <div className="absolute right-0 top-full mt-2 w-32 bg-white border rounded shadow-lg z-10">
                              <div
                                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(isAdmin ? `/Backend/Assessment/${item.vqa_request_id}/Manage` : `/joinVqa/${item.vqa_id}/Details`);
                                  setOpenDropdownId(null);
                                }}
                              >
                                View Details
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                ) : (
                  <div
                    className="flex flex-col items-center justify-center text-center space-y-2 min-h-[300px]"
                    style={{ marginTop: "100px" }}
                  >
                    <span className="p-3 rounded-full bg-custom-link-bg-color">
                      <MagnifyingGlassIcon
                        width={20}
                        height={20}
                        style={{ color: "white" }}
                      />
                    </span>
                    <p className="text-lg text-gray-700 font-semibold">
                      No Assessments Scheduled
                    </p>
                    <p className="text-gray-500">
                      Your search did not match any Assessments. Please try
                      again.
                    </p>
                  </div>
                )}
              </div>
              {/* Calendar Column */}
              <div className="text-center">
                <div style={{ height: "600px" }}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    popup
                    tooltipAccessor={(event) => event.title}
                    views={["month"]}
                    onNavigate={handleMonthChange}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={handleEventClick}
                    components={{
                      toolbar: ({ onNavigate, label }) => (
                        <div className="flex justify-between items-center mb-5">
                          <button
                            onClick={() => onNavigate("PREV")}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <ChevronLeftIcon className="h-6 w-6" />
                          </button>
                          <span className="text-lg font-medium">{label}</span>
                          <button
                            onClick={() => onNavigate("NEXT")}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <ChevronRightIcon className="h-6 w-6" />
                          </button>
                        </div>
                      ),
                      month: {
                        weekday: ({ label }) => (
                          <div className="text-sm font-bold text-gray-500 text-center">
                            {label[0]}
                          </div>
                        ),
                      },
                    }}
                    formats={{
                      dateFormat: "d",
                      weekdayFormat: (date, culture, localizer) =>
                        localizer.format(date, "E", culture),
                    }}
                    firstDay={1}
                  />
                  {!hideRequestActions && (
                    <div className="mt-5">
                      <CustomButton
                        color="bg-indigo-600"
                        textColor="text-white"
                        width="w-full"
                        text="Request Assessment"
                        onClick={() => navigate("/Assessment/Create")}
                        height="h-10"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentCalendar;
